import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PostureStatisticsBarChart from './PostureStatisticsBarChart';
import PostureStatisticsCircleChart from './PostureStatisticsCircleChart';
import PostureStatisticsRatingChart from './PostureStatisticsRatingChart';
import PostureStatisticsReverseBarChart from './PostureStatisticsReverseBarChart';
import Layout from 'components/Layout/Layout';
import NoDataText from 'components/Text/NoDataText';
import usePostureXlsx from 'hooks/usePostureXlsx';
import usePostureXlsxData from 'hooks/usePostureXlsxData';
import usePostureSchoolsXlsxData from 'hooks/usePostureSchoolsXlsxData';
import usePostureSchoolsXlsx from 'hooks/usePostureSchoolsXlsx';
import { titleColor } from 'asserts/styles/colors';
import { useAtomValue } from 'jotai';
import centerInforIdxAtom from 'atom/centerInforIdxAtom';
import companyIdxAtom from 'atom/companyIdxAtom';
import productAtom from 'atom/productAtom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import commonYearAtom from 'atom/commonYear';
import commonMonthAtom from 'atom/commonMonth';

const PostureStatisticsLayout = () => {
  const targetRef = useRef<HTMLDivElement | null>(null);

  const centerIdx = useAtomValue(centerInforIdxAtom);
  const selectedYear = useAtomValue(commonYearAtom);
  const selectedMonth = useAtomValue(commonMonthAtom);
  const companyIdx = useAtomValue(companyIdxAtom);
  const productNumber = useAtomValue(productAtom);

  const param = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth
  };

  const excelParam = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth,
    field: 'excel'
  };

  const queryKey = [QueryKeys.POSTURECHART, param];

  const getChartData = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/posture/stats',
        params: param
      }),
    enabled: false
  });

  const getChartDetailData = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/posture/stats/detail',
        params: excelParam
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        if (productNumber === 9 || productNumber === 8) {
          const xlsxData = usePostureSchoolsXlsxData(
            data?.excel,
            productNumber
          );
          usePostureSchoolsXlsx(xlsxData, productNumber);
        } else {
          const xlsxData = usePostureXlsxData(data?.excel, productNumber);
          usePostureXlsx(xlsxData, productNumber);
        }
      }
    },
    onError: (error: any) => {
      console.log(error);
    }
  });

  const { data: chartData, refetch: chartDataRefetch } = getChartData;

  const handleClickExcelDownload = async () => {
    getChartDetailData.mutate();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        if (entries[0].isIntersecting) {
          chartDataRefetch();
          observerInstance.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.1
      }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [chartDataRefetch, selectedYear, selectedMonth]);

  return (
    <>
      <Layout
        title="Posture 분석 통계"
        onExcelDownload={handleClickExcelDownload}
        result={chartData?.result}
      >
        <div ref={targetRef}>
          {chartData?.result === 'success' ? (
            <ChartWrapper>
              <PostureStatisticsBarChart chartData={chartData.posture} />
              <PostureStatisticsCircleChart chartData={chartData.posture} />
              <PostureStatisticsReverseBarChart chartData={chartData.posture} />
              <PostureStatisticsRatingChart chartData={chartData.posture} />
            </ChartWrapper>
          ) : (
            <NoDataText />
          )}
        </div>
      </Layout>
    </>
  );
};

export default PostureStatisticsLayout;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-flow: wrap;

  > div {
    transition: background-color 0.25s ease-in-out;
  }

  > div:hover {
    background-color: ${titleColor.lightMobileBackGround};
  }
`;
